import type { AxiosInstance } from 'axios';
import type { SupplierTermsStatus } from '@accio3d/shared/domain';

export type OrgRole = 'admin' | 'member' | 'viewer';

// Shapes mirror the backend serializers in
// server/api/src/organizations/organizations.service.ts (toMembershipResponse /
// toInvitationResponse).
export interface OrganizationMember {
  id: string; // membership id
  role: OrgRole;
  createdAt: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
  };
}

export interface OrganizationInvitation {
  id: string;
  email: string;
  role: OrgRole;
  status: 'pending' | 'accepted';
  expiresAt: string;
  acceptedAt: string | null;
  invitedById: string;
  createdAt: string;
}

export interface PaginatedOrganizationMembers {
  data: OrganizationMember[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface InviteTeamMemberRequest {
  email: string;
  role: OrgRole;
}

export interface UpdateOrganizationRequest {
  name?: string;
  website?: string | null;
  industry?: string | null;
  dunsNumber?: string | null;
}

export interface OrganizationDetails {
  id: string;
  name: string;
  type: string;
  website: string | null;
  industry: string | null;
  dunsNumber: string | null;
}

export function createOrganizationsService(client: AxiosInstance) {
  return {
    /** Retrieves the active organization's supplier agreement acceptance. */
    async getSupplierTerms(): Promise<SupplierTermsStatus> {
      const { data } = await client.get<SupplierTermsStatus>(
        '/organizations/current/supplier-terms',
      );
      return data;
    },

    /** Records explicit acceptance of the displayed agreement URL. */
    async acceptSupplierTerms(organizationId: string, url: string): Promise<SupplierTermsStatus> {
      const { data } = await client.post<SupplierTermsStatus>(
        '/organizations/current/supplier-terms',
        {
          organizationId,
          url,
          accepted: true,
        },
      );
      return data;
    },

    async getCurrent(): Promise<OrganizationDetails> {
      const { data } = await client.get<OrganizationDetails>('/organizations/current');
      return data;
    },

    async updateCurrent(body: UpdateOrganizationRequest): Promise<OrganizationDetails> {
      const { data } = await client.patch<OrganizationDetails>('/organizations/current', body);
      return data;
    },

    async getMembers(): Promise<PaginatedOrganizationMembers> {
      const { data } = await client.get<PaginatedOrganizationMembers>(
        '/organizations/current/members',
      );
      return data;
    },

    async getInvitations(): Promise<OrganizationInvitation[]> {
      const { data } = await client.get<OrganizationInvitation[]>(
        '/organizations/current/invitations',
      );
      return data;
    },

    async inviteMember(body: InviteTeamMemberRequest): Promise<OrganizationInvitation> {
      const { data } = await client.post<OrganizationInvitation>(
        '/organizations/current/invitations',
        body,
      );
      return data;
    },

    async removeMember(membershipId: string): Promise<void> {
      await client.delete(`/organizations/current/members/${encodeURIComponent(membershipId)}`);
    },

    async cancelInvitation(invitationId: string): Promise<void> {
      await client.delete(`/organizations/current/invitations/${encodeURIComponent(invitationId)}`);
    },

    async resendInvitation(invitationId: string): Promise<OrganizationInvitation> {
      const { data } = await client.post<{ token: string; response: OrganizationInvitation }>(
        `/organizations/current/invitations/${encodeURIComponent(invitationId)}/resend`,
      );
      return data.response;
    },
  };
}

export type OrganizationsService = ReturnType<typeof createOrganizationsService>;
